import './ApplicationGroupDependencyGraph.scss';

import { AppDependency, ApplicationGroup, ApplicationToServer, DatabaseApplication, DeserializedApplication } from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, withFlashContext } from '@amzn/awscat-react-components';
import { Box, Container, Header, NonCancelableCustomEvent, SpaceBetween, Spinner, Tabs, TabsProps } from '@amzn/awsui-components-react';
import { useEffect, useState } from 'react';
import VisGraph, { Edge, GraphData, GraphEvents, Network, Node, Options } from 'react-vis-graph-wrapper';

import { APP_DATABASE_EDGE_COLOR, APP_SERVER_EDGE_COLOR, SERVER_COMMUNICATION_EDGE_COLOR, USER_IMPORTED_EDGE_COLOR } from '../../../common/colors';
import { constants } from '../../../common/Localization';
import AppApp from '../../../common/vis-network-graph/images/icon-appApp.svg';
import AppDB from '../../../common/vis-network-graph/images/icon-appDB.svg';
import AppIcon from '../../../common/vis-network-graph/images/icon-applications.svg';
import AppServer from '../../../common/vis-network-graph/images/icon-appServer.svg';
import DatabaseIcon from '../../../common/vis-network-graph/images/icon-databases.svg';
import hiddenApp from '../../../common/vis-network-graph/images/icon-hidden-applications.svg';
import HiddenDatabaseIcon from '../../../common/vis-network-graph/images/icon-hidden-databases.svg';
import HiddenServerIcon from '../../../common/vis-network-graph/images/icon-hidden-servers.svg';
import ServerComm from '../../../common/vis-network-graph/images/icon-serverComm.svg';
import ServerIcon from '../../../common/vis-network-graph/images/icon-servers.svg';
import ApplicationGroupDependencyGraphResourceDetail from './ApplicationGroupDependencyGraphResourceDetail';
import ApplicationGroupDependencyHeatMap from './ApplicationGroupDependencyHeatMap';
import { DEPENDENCY_STATUS, DependencyDetail } from './constants';

type Props = {
    appDependencies: AppDependency[] | null;
    groupDetail: ApplicationGroup | null;
    appServerMappings: ApplicationToServer[];
    appDatabaseMappings: DatabaseApplication[];
    loadingGroupDetail: boolean;
    onGroupDetailUpdate: (updatedGroup: ApplicationGroup) => void;
} & FlashContextInterface;
  
const options: Options = {
    height: '100%',
    interaction: {
        hideEdgesOnDrag: true,
        selectConnectedEdges: false,
    },
    physics: {
        enabled: false,
    },
    autoResize: true,
};

const MAX_DEPENDENCIES_IN_GRAPH = 1000;

function ApplicationGroupDependencyGraph(
    { appDependencies, groupDetail, loadingGroupDetail, onGroupDetailUpdate, appServerMappings, appDatabaseMappings }
: Props) {

    const [ activeVisualizationTab, setActiveVisualizationTab ] = useState('dependencyGraph');
    const [ dependencyMap, setDependencyMap ] = useState<Map<string, DependencyDetail>>(new Map());
    const [ groupDependencies, setGroupDependencies ] = useState<AppDependency[] | null>(null);
    const [ selectedDependency, setSelectedDependency ] = useState<DependencyDetail | null>(null);
    const [ networkGraphData, setNetworkGraphData ] = useState<GraphData>({nodes: [], edges: []});

    const [ network, setNetwork ] = useState<Network>();

    useEffect(() => {
        if (network) {
            network.fit();
            network.stabilize(500);
        }
    }, [ network ]);

    const containerHeader = (<Header variant='h2'>{constants.VISUALIZATION}</Header>);

    const onResourceHideToggle = (resource: DependencyDetail, hidden: boolean) => {
        const { resourceId } = resource;
        const resourceMapKey = resource.type === 'server' ? `S ${resourceId}` : resource.type === 'database' ? `D ${resourceId}` : `A ${resourceId}`;
        const dependencyDetail = dependencyMap.get(resourceMapKey);
        if (dependencyDetail) {
            const dependencyDetailMap = new Map(dependencyMap);
            dependencyDetail.hidden = hidden;
            dependencyDetailMap.set(resourceMapKey, dependencyDetail);
            setDependencyMap(dependencyDetailMap);
        }
    };

    useEffect(() => {
        if (groupDetail && groupDependencies) {
            setNetworkGraphData(getFormattedGraphData(groupDetail.applications, groupDependencies, dependencyMap));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ dependencyMap ]);

    const buildDependencyMap = (allApps: DeserializedApplication[], dependencies: AppDependency[]): void => {
        const dependencyDetailMap: Map<string, DependencyDetail> = new Map();
        allApps.forEach(application => {
            const appId = `A ${application.applicationId}`;
            dependencyDetailMap.set(appId, {
                resourceId: application.applicationId,
                type: 'application',
                hidden: false,
                applications: new Set(),
                servers: new Set(),
                databases: new Set(),
            });
        });

        const filteredDependencies = dependencies.filter(dependency => 
            dependency.status === DEPENDENCY_STATUS.ACTIVE && dependencyDetailMap.has(`A ${dependency.srcAppId}`)
                && dependencyDetailMap.has(`A ${dependency.destAppId}`)
        );

        setGroupDependencies(filteredDependencies);

        filteredDependencies.forEach(dependency => {
            const { resourceType, srcAppId, destAppId } = dependency;
            const srcAppDetail = dependencyDetailMap.get(`A ${srcAppId}`);
            const destAppDetail = dependencyDetailMap.get(`A ${destAppId}`);

            switch (resourceType) {
                case 'applications-servers-v0':
                    const serverId = dependency.resourceId;
                    if (srcAppDetail) {
                        srcAppDetail.servers.add(serverId);
                        dependencyDetailMap.set(`A ${srcAppId}`, srcAppDetail);
                    }
                    const serverDetail = dependencyDetailMap.get(`S ${serverId}`) ?? {
                        type: 'server',
                        resourceId: serverId,
                        hidden: false,
                        applications: new Set(),
                        servers: new Set(),
                        databases: new Set(),
                    };
                    serverDetail.applications.add(srcAppId);
                    dependencyDetailMap.set(`S ${serverId}`, serverDetail);
                    break;
                case 'user-import':
                case 'server-communications-v0':
                    if (srcAppDetail) {
                        srcAppDetail.applications.add(destAppId);
                        dependencyDetailMap.set(`A ${srcAppId}`, srcAppDetail);
                    }
                    if (destAppDetail) {
                        destAppDetail.applications.add(srcAppId);
                        dependencyDetailMap.set(`A ${destAppId}`, destAppDetail);
                    }
                    break;
                case 'databases-applications-v0':
                    const databaseId = dependency.resourceId;
                    if (srcAppDetail) {
                        srcAppDetail.databases.add(databaseId);
                        dependencyDetailMap.set(`A ${srcAppId}`, srcAppDetail);
                    }
                    const databaseDetail = dependencyDetailMap.get(`D ${databaseId}`) ?? {
                        type: 'database',
                        resourceId: databaseId,
                        hidden: false,
                        applications: new Set(),
                        servers: new Set(),
                        databases: new Set(),
                    };
                    databaseDetail.applications.add(srcAppId);
                    dependencyDetailMap.set(`D ${databaseId}`, databaseDetail);
                    break;
                default:
                    break;
            }
        });

        setDependencyMap(dependencyDetailMap);
    };

    useEffect(() => {
        if (groupDetail && appDependencies) {
            buildDependencyMap(groupDetail.applications, appDependencies);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ appDependencies, groupDetail ]);

    const getFormattedGraphData = (
        allApps: DeserializedApplication[], dependencies: AppDependency[], dependencyDetailMap: Map<string, DependencyDetail>,
    ): GraphData => {
        const nodes: Node[] = [];
        const edges: Edge[] = [];

        const applicationsMap: Record<string, Record<string, boolean>> = {};
        const serversMap: Record<string, Record<string, boolean>> = {};
        const databaseMap: Record<string, Record<string, boolean>> = {};

        allApps.forEach(application => {
            const appId = `A ${application.applicationId}`;
            const appDependencyDetail = dependencyDetailMap.get(appId);
            const title = appDependencyDetail &&
                `${constants.CONNECTED_APPS} ${appDependencyDetail.applications.size}\r\n${constants.CONNECTED_SERVERS} ${appDependencyDetail.servers.size}`
                + `\r\n${constants.CONNECTED_DBS} ${appDependencyDetail.databases.size}`;
            nodes.push({
                id: appId,
                label: application.name,
                title,
                size: 20,
                shape: 'image',
                image: appDependencyDetail?.hidden ? hiddenApp : AppIcon,
                shadow: appDependencyDetail?.hidden ? false : true,
            });
            if (!appDependencyDetail?.hidden) {
                applicationsMap[appId] = {};
            }
        });


        const dependenciesToProcess = dependencies.length > MAX_DEPENDENCIES_IN_GRAPH ? dependencies.slice(0, MAX_DEPENDENCIES_IN_GRAPH) : dependencies;

        dependenciesToProcess.forEach(dependency => {
            const { resourceType, resourceId, isSharedInfrastructure } = dependency;
            const srcAppId = `A ${dependency.srcAppId}`;
            const destAppId = `A ${dependency.destAppId}`;
            if (!isSharedInfrastructure && !!applicationsMap[srcAppId] && !!applicationsMap[destAppId]) {
                switch (resourceType) {
                    case 'server-communications-v0':
                        if (!applicationsMap[srcAppId][destAppId]) {
                            edges.push({
                                from: srcAppId,
                                to: destAppId,
                                color: {color: SERVER_COMMUNICATION_EDGE_COLOR},
                                title: `${constants.SERVER_COMMUNICATION} ${constants.FROM} ${dependency.srcAppId} ${constants.TO} ${dependency.destAppId}`
                            
                            });
                            applicationsMap[srcAppId][destAppId] = true;
                        }
                        break;
                    case 'applications-servers-v0':
                        const serverId = `S ${resourceId}`;
                        const serverDetail = dependencyDetailMap.get(serverId);
                        if (serverDetail) {
                            const serverTitle = `${constants.CONNECTED_APPS} ${serverDetail.applications.size}`;
                            if (!serversMap[serverId]) {
                                if (!serverDetail.hidden) {
                                    nodes.push({
                                        id: serverId,
                                        label: resourceId,
                                        title: serverTitle,
                                        size: 15,
                                        shape: 'image',
                                        image: ServerIcon,
                                        shadow: true,
                                    });
                                    serversMap[serverId] = {};
                                } else {
                                    nodes.push({
                                        id: serverId,
                                        label: resourceId,
                                        title: serverTitle,
                                        size: 15,
                                        shape: 'image',
                                        image: HiddenServerIcon,
                                    });
                                    serversMap[serverId] = {};
                                }
                            }
                            if (!serverDetail.hidden && !serversMap[serverId][srcAppId]) {
                                edges.push({
                                    from: serverId,
                                    to: srcAppId,
                                    color: {color: APP_SERVER_EDGE_COLOR},
                                    title: `${constants.SERVER_DEPENDENCY} ${constants.BETWEEN} ${dependency.srcAppId} ${constants.AND} ${resourceId}`,
                                });
                                serversMap[serverId][srcAppId]  = true;
                            }
                        }
                        break;
                    case 'databases-applications-v0':
                        const dbId = `D ${resourceId}`;
                        const databaseDetail = dependencyDetailMap.get(dbId);
                        if (databaseDetail) {
                            const dbTitle = `${constants.CONNECTED_APPS} ${databaseDetail.applications.size}`;
                            if (!databaseMap[dbId]) {
                                if (!databaseDetail.hidden) {
                                    nodes.push({
                                        id: dbId,
                                        label: resourceId,
                                        title: dbTitle,
                                        size: 20,
                                        shape: 'image',
                                        image: DatabaseIcon,
                                        shadow: true,
                                    });
                                    databaseMap[dbId] = {};
                                } else {
                                    nodes.push({
                                        id: dbId,
                                        label: resourceId,
                                        title: dbTitle,
                                        size: 20,
                                        shape: 'image',
                                        image: HiddenDatabaseIcon,
                                    });
                                    databaseMap[dbId] = {};
                                }
                            }
                            if (!databaseDetail.hidden && !databaseMap[dbId][srcAppId]) {
                                edges.push({
                                    from: dbId,
                                    to: srcAppId,
                                    color: {color: APP_DATABASE_EDGE_COLOR},
                                    title: `${constants.DATABASE_DEPENDENCY} ${constants.BETWEEN} ${dependency.srcAppId} ${constants.AND} ${resourceId}`,
                                });
                                databaseMap[dbId][srcAppId]  = true;
                            }
                        }
                        break;
                    case 'user-import':
                        edges.push({
                            from: srcAppId,
                            to: destAppId,
                            color: {color: USER_IMPORTED_EDGE_COLOR},
                            title: `${constants.USER_IMPORTED_DEPENDENCY} ${constants.BETWEEN} ${dependency.srcAppId} ${constants.AND} ${dependency.destAppId}`
                        });
                        break;
                    default:
                        break;
                }
            }
        });

        return {
            nodes,
            edges,
        };
    };

    const buildDependencyGraph = (
        group: ApplicationGroup | null, dependencies: AppDependency[] | null, dependencyDetailMap: Map<string, DependencyDetail>,
    ): JSX.Element => {
        if (group && group.applications?.length && dependencies) {
            // type is any becuase the wrapper defines it as any
            const clickHandler = (params: any): void => {
                const selectedResource: string | null = (params.nodes?.[0] as string) ?? null;
                const resourceDetail = selectedResource ? dependencyDetailMap.get(selectedResource) : null;
                if (resourceDetail) {
                    setSelectedDependency(resourceDetail);
                }
                else {
                    setSelectedDependency(null);
                }
            };

            const events: Partial<GraphEvents> = {
                'select': clickHandler,
                'dragEnd': clickHandler,
                'click': clickHandler,
            };
            return (
                <Box>
                    <Box className='group-detail-dependency-graph' display='inline-block'>
                        <div id='dependency-graph' data-testid='dependency-graph-div'>
                            <div className='dependency-graph-legend'>
                                <SpaceBetween direction='horizontal' size='xl'>
                                    <Box textAlign='center'>
                                        <img src={AppIcon} height={'20px'} /> {constants.APPLICATION}
                                    </Box>
                                    <Box textAlign='center'>
                                        <img src={ServerIcon} height={'20px'} /> {constants.SERVER}
                                    </Box>
                                    <Box textAlign='center'>
                                        <img src={DatabaseIcon} height={'20px'} /> {constants.DATABASE}
                                    </Box>
                                    <Box textAlign='center'>
                                        <img src={AppServer} height={'5px'} width={'35px'} /> {constants.SERVER_DEPENDENCY}
                                    </Box>
                                    <Box textAlign='center'>
                                        <img src={AppDB} height={'5px'} width={'35px'} /> {constants.DATABASE_DEPENDENCY}
                                    </Box>
                                    <Box textAlign='center'>
                                        <img src={ServerComm} height={'5px'} width={'35px'} /> {constants.SERVER_COMMUNICATION}
                                    </Box>
                                    <Box textAlign='center'>
                                        <img src={AppApp} height={'5px'} width={'35px'} /> {constants.USER_IMPORTED_DEPENDENCY}
                                    </Box>
                                </SpaceBetween>
                            </div>
                            <VisGraph
                                graph={networkGraphData}
                                options={options}
                                events={events}
                                style={{ height: '625px' }}
                                getNetwork={network => setNetwork(network as Network)}
                            />
                        </div>
                    </Box>
                    <ApplicationGroupDependencyGraphResourceDetail
                        groupDetail={group}
                        selectedResource={selectedDependency}
                        onGroupDetailUpdate={onGroupDetailUpdate}
                        onResourceHideToggle={onResourceHideToggle}
                        dependencies={appDependencies}
                        appServerMappings={appServerMappings}
                        appDatabaseMappings={appDatabaseMappings}
                    />
                </Box>
            );
        }
        else if (loadingGroupDetail) {
            return ( <Spinner size='large' /> );
        }
        return (
            <p className='dependency-graph-text-align'>{constants.NO_DEPENDENCY_TO_VISUALIZE}</p>
        );
    };

    const buildTabs = (
        group: ApplicationGroup | null, dependencies: AppDependency[] | null, dependencyDetailMap: Map<string, DependencyDetail>,
    ): TabsProps.Tab[] => [
        {
            id: 'dependencyGraph',
            label: constants.DEPENDENCY_GRAPH,
            content: buildDependencyGraph(groupDetail, groupDependencies, dependencyDetailMap),
                
        },
        {
            id: 'heatmap',
            label: constants.HEATMAP,
            content: <ApplicationGroupDependencyHeatMap
                loading={loadingGroupDetail}
                selectedDependency={selectedDependency}
                onGroupDetailUpdate={onGroupDetailUpdate}
                onResourceHideToggle={onResourceHideToggle}
                appDependencies={appDependencies}
                appServerMappings={appServerMappings}
                appDatabaseMappings={appDatabaseMappings}
                group={group}
                dependencies={dependencies}
                dependencyDetailMap={dependencyDetailMap}
            />
        },
    ];

    const onTabChange = ({ detail }: NonCancelableCustomEvent<TabsProps.ChangeDetail>) => {
        const newTab = detail.activeTabId;
        setActiveVisualizationTab(newTab);
        if (newTab === 'heatmap') {
            setSelectedDependency(null);
        }
    };

    return (
        <Container data-testid='applicationGroupDependencyGraph' id='visualization-container' header={containerHeader}>
            <Tabs
                tabs={buildTabs(groupDetail, groupDependencies, dependencyMap)}
                activeTabId={activeVisualizationTab}
                onChange={onTabChange}
            />
        </Container>
    );
}

export default withFlashContext(ApplicationGroupDependencyGraph);
